import type { Options } from './wasmBridge/viewModel';
import type { PuzzleId } from '../components/luna-common';
import { decodePresetOptions, sameOptions, type Preset } from './preset';
import { validateOptions, type GameOptionDefinition } from './settings-catalog';

/** The gameplay presentation-state machine (BR-69/70). */
export type GameplayState = 'loading' | 'playing' | 'solved' | 'error' | 'empty';

/** The designed control-state language from the States frames (BR-69). */
export type ControlState = 'Pressable' | 'Disabled' | 'Solved';

/** The status chip content (BR-62). */
export interface GameplayStatusLabel {
  text: string;
  state: 'playing' | 'solved';
}

/**
 * The resolution of a gameplay URL (level / saved / custom / redirect).
 * Invalid or bare URLs resolve to a Picker redirect (BR-59); unknown puzzles
 * are handled earlier by the route guard.
 */
export type GameStartResolution =
  | { kind: 'resume' }
  | { kind: 'level'; level: number }
  | { kind: 'saved'; preset: Preset }
  | { kind: 'custom'; options: Options[]; label?: string }
  | { kind: 'redirect'; path: string };

/**
 * Resolve how to start a game from the URL (BR-59/50/67). Pure and total:
 * malformed `?saved=`/`?custom=`, unknown saved presets, and invalid levels all
 * redirect to the Picker; a bare no-level/no-param URL also redirects.
 */
export function resolveGameStart(
  search: string,
  level: number | null | undefined,
  presets: Preset[],
  presetIndices: number[],
  definitions: GameOptionDefinition[],
  puzzleName: PuzzleId
): GameStartResolution {
  const picker = (): GameStartResolution => ({ kind: 'redirect', path: `/puzzles/${puzzleName}/boards` });
  const params = new URLSearchParams(search);

  if (params.get('resume') === '1') {
    return { kind: 'resume' };
  }

  const savedParam = params.get('saved');
  if (savedParam !== null) {
    const options = decodePresetOptions(savedParam);
    if (!options) {
      return picker();
    }
    const preset = presets.find((candidate) => sameOptions(candidate.options, options));
    if (!preset) {
      return picker();
    }
    return { kind: 'saved', preset };
  }

  const customParam = params.get('custom');
  if (customParam !== null) {
    const options = decodePresetOptions(customParam);
    if (!options || !validateOptions(definitions, options).valid) {
      return picker();
    }
    return { kind: 'custom', options, label: params.get('label') ?? undefined };
  }

  if (level !== null && level !== undefined) {
    if (!Number.isInteger(level) || level < 0 || !presetIndices.includes(level)) {
      return picker();
    }
    return { kind: 'level', level };
  }

  return picker();
}

/**
 * Leave-save gate (BR-127/128): a game is only persisted while actively playing,
 * after at least one move, and when the current save string differs from what was
 * last saved. The caller reads the save string once and reuses it for the payload.
 */
export function shouldSaveOnLeave(
  gameplayState: GameplayState,
  hasMoves: boolean,
  state: string,
  lastSavedState: string | null
): boolean {
  return gameplayState === 'playing' && hasMoves && state !== lastSavedState;
}
